import React, { useState, useEffect } from 'react';
import api from '../config/api';
import { FiPlus, FiUserPlus, FiTrash2, FiDownload, FiX, FiCopy, FiCheck, FiRefreshCw } from 'react-icons/fi';

/**
 * B2B Agent management panel.
 * Admin can register new agents, list registered agents, open an agent's
 * bookings in a modal (date, guest, villa, days split, booking total excl
 * deposit, commission). Also exposes a shareable confirmation message so the
 * admin can forward registration details to the agent on WhatsApp.
 */
const B2BAgentsPanel = () => {
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', phone: '' });
  const [formErr, setFormErr] = useState('');
  const [regShare, setRegShare] = useState(null); // {agent, share_message}
  const [copied, setCopied] = useState(false);

  const [modalAgent, setModalAgent] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);

  const loadAgents = async () => {
    setBusy(true);
    try {
      const res = await api.get('/admin/b2b-agents');
      setAgents(res.data || []);
    } catch { /* ignore */ }
    setBusy(false);
  };

  useEffect(() => { loadAgents(); }, []);

  const handleRegister = async (e) => {
    e?.preventDefault?.();
    setFormErr('');
    setRegShare(null);
    if (!form.code.trim() || !form.phone.trim()) { setFormErr('Agent code and phone are required'); return; }
    try {
      const res = await api.post('/admin/b2b-agents', form);
      setRegShare(res.data);
      setForm({ code: '', name: '', phone: '' });
      loadAgents();
    } catch (err) {
      setFormErr(err?.response?.data?.detail || 'Registration failed');
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Deregister agent "${code}"? Their past bookings remain in records.`)) return;
    await api.delete(`/admin/b2b-agents/${id}`);
    loadAgents();
  };

  const openAgentBookings = async (agent) => {
    setModalAgent(agent);
    setModalData(null);
    setModalBusy(true);
    try {
      const res = await api.get(`/admin/b2b-agents/${agent.id}/bookings`);
      setModalData(res.data);
    } catch { setModalData({ bookings: [], commission_grand_total: 0, agent }); }
    setModalBusy(false);
  };

  const exportCSV = () => {
    if (!modalData?.bookings?.length) return;
    const header = ['Booking Date', 'Guest', 'Villa', 'Check-in', 'Check-out', 'Weekend nights', 'Weekday nights', 'Booking Total (excl deposit)', 'Commission', 'Rule', 'Status'];
    const rows = modalData.bookings.map(b => [
      b.created_at?.slice(0, 10), b.guest_name, b.villa_name, b.check_in, b.check_out,
      b.weekend_nights_count ?? 0, b.weekday_nights_count ?? 0,
      b.booking_total_excl_deposit, b.commission_total, b.commission_rule,
      `${b.booking_status || ''}/${b.payment_status || ''}`,
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${(c ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${modalData.agent?.code}-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const copyShare = () => {
    if (!regShare?.share_message) return;
    navigator.clipboard.writeText(regShare.share_message).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-8 bg-[#1B2432] rounded-xl p-8" data-testid="b2b-agents-section">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-start gap-3">
          <FiUserPlus className="w-5 h-5 text-[#F57C20] mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-light text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>B2B Agent Bookings</h3>
            <p className="text-white/60 text-sm">Register travel agents, share codes, track their bookings &amp; commissions.</p>
          </div>
        </div>
        <button data-testid="b2b-refresh-btn" onClick={loadAgents} disabled={busy}
          className="bg-white/10 hover:bg-white/15 text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
          <FiRefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Register new agent */}
      <form onSubmit={handleRegister} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-5">
        <p className="text-white text-sm font-semibold mb-3 flex items-center gap-2"><FiPlus className="w-4 h-4 text-[#F57C20]" /> Register New Agent</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <input data-testid="b2b-reg-code" placeholder="Agent Code (e.g. RAHUL01)" value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            className="bg-[#111] border border-white/20 text-white text-sm rounded-lg px-3 py-2 uppercase placeholder:text-white/40 focus:outline-none focus:border-[#F57C20]" />
          <input data-testid="b2b-reg-name" placeholder="Agent / Agency Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#111] border border-white/20 text-white text-sm rounded-lg px-3 py-2 placeholder:text-white/40 focus:outline-none focus:border-[#F57C20]" />
          <input data-testid="b2b-reg-phone" placeholder="Mobile (10 digits)" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="bg-[#111] border border-white/20 text-white text-sm rounded-lg px-3 py-2 placeholder:text-white/40 focus:outline-none focus:border-[#F57C20]" />
        </div>
        {formErr && <p data-testid="b2b-reg-err" className="text-red-400 text-xs mb-2">{formErr}</p>}
        <button data-testid="b2b-reg-submit" type="submit"
          className="bg-[#F57C20] hover:bg-[#E06A10] text-white text-sm font-semibold px-4 py-2 rounded-lg">
          Register Agent
        </button>
      </form>

      {/* Registration confirmation / shareable message */}
      {regShare && (
        <div data-testid="b2b-reg-confirmation" className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-5">
          <p className="text-emerald-300 text-sm font-semibold mb-2">✅ Agent registered — share this message with your agent:</p>
          <pre className="whitespace-pre-wrap text-white/90 text-xs bg-black/30 rounded-lg p-3 font-mono">{regShare.share_message}</pre>
          <div className="flex gap-2 mt-3">
            <button onClick={copyShare} data-testid="b2b-copy-share"
              className="bg-white/10 hover:bg-white/15 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5">
              {copied ? <><FiCheck className="w-3.5 h-3.5" /> Copied!</> : <><FiCopy className="w-3.5 h-3.5" /> Copy</>}
            </button>
            <a href={`https://wa.me/91${regShare.agent.phone}?text=${encodeURIComponent(regShare.share_message)}`}
              target="_blank" rel="noreferrer"
              className="bg-[#25D366] hover:bg-[#1DA851] text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5">
              📲 WhatsApp to agent
            </a>
            <button onClick={() => setRegShare(null)} className="text-white/50 text-xs hover:text-white">Dismiss</button>
          </div>
        </div>
      )}

      {/* Registered agents list */}
      <div data-testid="b2b-agents-list">
        {agents.length === 0 && !busy ? (
          <p className="text-white/40 text-sm">No agents registered yet. Register your first agent above.</p>
        ) : (
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {agents.map(a => (
              <div key={a.id} data-testid={`b2b-agent-row-${a.code}`}
                className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/10 transition-colors cursor-pointer"
                onClick={() => openAgentBookings(a)}>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">{a.code} <span className="text-white/50 font-normal">· {a.name}</span></p>
                  <p className="text-white/50 text-xs">+91 {a.phone} · Registered {a.created_at?.slice(0, 10)}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(a.id, a.code); }}
                  className="text-white/30 hover:text-red-400 p-1.5">
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Agent-bookings modal */}
      {modalAgent && (
        <div data-testid="b2b-agent-modal" className="fixed inset-0 z-[80] bg-black/70 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setModalAgent(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-[#1B2432] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[#F57C20] text-[11px] font-bold tracking-widest uppercase mb-0.5">Agent Bookings</p>
                <h3 className="text-xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {modalAgent.code} <span className="text-white/60 text-sm font-normal">· {modalAgent.name} · +91 {modalAgent.phone}</span>
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {modalData?.bookings?.length > 0 && (
                  <button onClick={exportCSV} data-testid="b2b-modal-export"
                    className="bg-[#F57C20] hover:bg-[#E06A10] text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5">
                    <FiDownload className="w-3.5 h-3.5" /> CSV
                  </button>
                )}
                <button onClick={() => setModalAgent(null)} className="text-white/60 hover:text-white p-2">
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto">
              {modalBusy ? (
                <p className="text-[#6B7280] text-sm">Loading bookings…</p>
              ) : !modalData?.bookings?.length ? (
                <p className="text-[#6B7280] text-sm">No bookings yet via this agent.</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-[#F7F8FA] rounded-lg px-4 py-3">
                      <p className="text-[#6B7280] text-[10px] uppercase tracking-wider">Total Bookings</p>
                      <p className="text-[#1A1A2E] text-xl font-semibold">{modalData.bookings.length}</p>
                    </div>
                    <div className="bg-[#F7F8FA] rounded-lg px-4 py-3">
                      <p className="text-[#6B7280] text-[10px] uppercase tracking-wider">Total Booking Value</p>
                      <p className="text-[#1A1A2E] text-xl font-semibold">
                        Rs.{modalData.bookings.reduce((s, b) => s + (b.booking_total_excl_deposit || 0), 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="bg-[#FFF7EC] border border-[#F57C20]/30 rounded-lg px-4 py-3">
                      <p className="text-[#F57C20] text-[10px] uppercase tracking-wider font-bold">Total Commission</p>
                      <p className="text-[#F57C20] text-xl font-semibold">
                        Rs.{(modalData.commission_grand_total || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#F7F8FA] text-[#6B7280] text-[10px] uppercase tracking-wider">
                          <th className="text-left px-3 py-2">Booking Date</th>
                          <th className="text-left px-3 py-2">Guest</th>
                          <th className="text-left px-3 py-2">Villa</th>
                          <th className="text-left px-3 py-2">Days (Wkend/Wkday)</th>
                          <th className="text-right px-3 py-2">Total (excl. deposit)</th>
                          <th className="text-right px-3 py-2">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalData.bookings.map(b => (
                          <tr key={b.booking_id} className="border-b border-gray-100">
                            <td className="px-3 py-2 text-[#374151] text-xs">{b.created_at?.slice(0, 10)}</td>
                            <td className="px-3 py-2 text-[#1A1A2E] font-medium">{b.guest_name || '—'}</td>
                            <td className="px-3 py-2 text-[#374151] text-xs">{b.villa_name}</td>
                            <td className="px-3 py-2 text-[#374151] text-xs">
                              <span className="text-[#F57C20]">{b.weekend_nights_count || 0}W</span>
                              <span className="text-[#6B7280]"> / {b.weekday_nights_count || 0}D</span>
                            </td>
                            <td className="px-3 py-2 text-right text-[#374151]">Rs.{(b.booking_total_excl_deposit || 0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right text-[#F57C20] font-semibold">Rs.{(b.commission_total || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BAgentsPanel;
